import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTemporada } from '../../context/TemporadaContext';
import { useAuth } from '../../context/AuthContext';
import { useCalendarioDetalle } from '../../hooks/useCalendarioDetalle';
import {
  getPistasJornada, reemplazarDisponibilidad, asignarPista, publicarJornada, resetearJornada,
  getDatosJornadas, guardarDatosJornada,
} from '../../services/asignacionPistaService';
import { derivarFranjas, mapearPartidos, computeConflictos, proponerReparto, MODALIDADES } from './logica';
import FilaFranja from './FilaFranja';
import PartidoCard from './PartidoCard';
import AplazadosJustificados, { AplazadosDeJornada, aplazadosDeJornada, aplazadosJustificados } from './AplazadosJustificados';
import AdelantarPartidos from './AdelantarPartidos';
import DisponibilidadPanel from './DisponibilidadPanel';
import './CompeticionPistas.css';

const PISTAS = [1, 2, 3, 4];

// Pantalla real de asignación de partidos a pistas — sustituye al antiguo
// CompeticionDragAndDrop en /dashboard/competicion-2. Ver
// docs/PLAN_COMPETICION_PISTAS.md.
const CompeticionPistas = () => {
  const { temporada } = useTemporada();
  const { usuario } = useAuth();
  const editable = usuario?.rol === 'ADMIN';
  const puedeEditarDatos = usuario?.rol === 'ADMIN' || usuario?.rol === 'COMITE';

  const { partidos: calendario, rosterPorParticipante, cargando: cargandoCalendario, error: errorCalendario } = useCalendarioDetalle(temporada);

  // Una jornada real (temporada_id + jornada) puede reunir IDA de una
  // modalidad y VUELTA de otra: roundRobin.ts no reinicia la numeración en
  // la vuelta, la continúa donde acabó la ida. Por eso se agrupa solo por
  // número de jornada, no por (jornada, tipo) — ver badminton-api
  // docs/DOCUMENTACION_BD.md §2.
  const jornadasReales = useMemo(() => {
    const vistos = new Set();
    calendario.forEach((p) => vistos.add(p.jornada));
    return [...vistos].sort((a, b) => a - b).map((jornada) => ({ jornada }));
  }, [calendario]);

  const [jornadaSel, setJornadaSel] = useState(null);
  useEffect(() => {
    if (jornadasReales.length && (!jornadaSel || !jornadasReales.some((j) => j.jornada === jornadaSel.jornada))) {
      setJornadaSel(jornadasReales[0]);
    }
  }, [jornadasReales, jornadaSel]);

  const [datosPistas, setDatosPistas] = useState(null);
  const [cargandoPistas, setCargandoPistas] = useState(false);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [guardandoDisponibilidad, setGuardandoDisponibilidad] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [pestana, setPestana] = useState('pendientes');

  const cargarPistas = useCallback(() => {
    if (!temporada || !jornadaSel) return;
    setCargandoPistas(true);
    setError(null);
    getPistasJornada(temporada, jornadaSel.jornada)
      .then(setDatosPistas)
      .catch((e) => setError(e.message))
      .finally(() => setCargandoPistas(false));
  }, [temporada, jornadaSel]);

  useEffect(() => { cargarPistas(); }, [cargarPistas]);

  // Fecha y sede de la jornada (cabecera de las descargas de /jornadas).
  const [datosJornada, setDatosJornada] = useState({ fecha: '', sede: '' });
  const [guardandoDatos, setGuardandoDatos] = useState(false);
  useEffect(() => {
    if (!temporada || !jornadaSel) return;
    getDatosJornadas(temporada)
      .then((lista) => {
        const d = lista.find((x) => x.jornada === jornadaSel.jornada);
        setDatosJornada({ fecha: d?.fecha ?? '', sede: d?.sede ?? '' });
      })
      .catch(() => setDatosJornada({ fecha: '', sede: '' }));
  }, [temporada, jornadaSel]);

  const handleGuardarDatos = async () => {
    setGuardandoDatos(true);
    try {
      await guardarDatosJornada(temporada, jornadaSel.jornada, {
        fecha: datosJornada.fecha || null,
        sede: datosJornada.sede.trim() || null,
      });
      setAviso('Fecha y sede de la jornada guardadas.');
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardandoDatos(false);
    }
  };

  const franjas = useMemo(() => (datosPistas ? derivarFranjas(datosPistas.disponibilidad) : []), [datosPistas]);
  const aplazadosPropios = useMemo(
    () => (jornadaSel ? aplazadosDeJornada(calendario, jornadaSel.jornada) : []),
    [calendario, jornadaSel],
  );
  // Un aplazado o un WO no se juega en su fecha: si aún está en el cuadrante se ve (para poder
  // sacarlo), pero sin colocar no entra en "pendientes" ni en la propuesta.
  const idsAplazados = useMemo(() => new Set(aplazadosPropios.map((p) => p.id)), [aplazadosPropios]);
  const partidos = useMemo(
    () => (datosPistas
      ? mapearPartidos(datosPistas.partidos, rosterPorParticipante)
          // Aplazados, WO y ya jugados solo se ven si siguen en el cuadrante; sin colocar no entran
          // en "pendientes" ni en la propuesta.
          .filter((p) => p.franjaId || (!idsAplazados.has(p.id) && !p.yaJugado))
      : []),
    [datosPistas, rosterPorParticipante, idsAplazados],
  );
  const aplazadosColocados = useMemo(() => {
    const mapa = {};
    partidos.forEach((p) => { if (p.franjaId && idsAplazados.has(p.id)) mapa[p.id] = { pista: p.pista, franjaId: p.franjaId }; });
    return mapa;
  }, [partidos, idsAplazados]);
  const conflictosPorCelda = useMemo(() => computeConflictos(partidos), [partidos]);
  const partidosPorCelda = useMemo(() => {
    const mapa = {};
    partidos.forEach((p) => { if (p.franjaId) mapa[`${p.pista}-${p.franjaId}`] = p; });
    return mapa;
  }, [partidos]);
  const pendientes = useMemo(() => partidos.filter((p) => !p.franjaId), [partidos]);

  const jornadasFuturas = useMemo(
    () => (jornadaSel ? jornadasReales.map((j) => j.jornada).filter((j) => j > jornadaSel.jornada) : []),
    [jornadasReales, jornadaSel],
  );
  const hayFuturas = jornadasFuturas.length > 0;

  const pestanas = [
    editable && { id: 'pendientes', titulo: `Partidos jornada (${pendientes.length})` },
    editable && hayFuturas && { id: 'adelantar', titulo: 'Adelantar partidos' },
    aplazadosPropios.length > 0 && { id: 'aplazados', titulo: `Partidos aplazados o wo (${aplazadosPropios.length})` },
  ].filter(Boolean);
  // Si la pestaña elegida ya no existe (p. ej. se vació la de aplazados), se cae a la primera.
  const pestanaActiva = pestanas.some((t) => t.id === pestana) ? pestana : pestanas[0]?.id;
  const hayConflictos = Object.keys(conflictosPorCelda).length > 0;

  const handleDropPartido = async (partidoId, pista, franjaId) => {
    if (!editable || !datosPistas) return;
    setAviso(null);
    const franja = franjas.find((f) => f.id === franjaId);
    if (!franja) return;
    const destino = datosPistas.partidos.find((p) => p.pista === pista && p.hora_inicio === franjaId && p.id !== partidoId);

    setProcesando(true);
    try {
      if (destino) await asignarPista(destino.id, { pista: null, horaInicio: null, horaFin: null });
      await asignarPista(partidoId, { pista, horaInicio: franjaId, horaFin: franja.horaFin, jornadaCuadrante: jornadaSel.jornada });
    } catch (e) {
      setError(e.message);
    } finally {
      setProcesando(false);
      cargarPistas();
    }
  };

  const handleMoverFranja = async (origenId, destinoId) => {
    if (!editable || !datosPistas || origenId === destinoId) return;
    setAviso(null);
    const franjaOrigen = franjas.find((f) => f.id === origenId);
    const franjaDestino = franjas.find((f) => f.id === destinoId);
    if (!franjaOrigen || !franjaDestino) return;

    const partidosOrigen = datosPistas.partidos.filter((p) => p.hora_inicio === origenId);
    const partidosDestino = datosPistas.partidos.filter((p) => p.hora_inicio === destinoId);

    setProcesando(true);
    let sinSitio = 0;
    try {
      // Desasignar primero todo lo implicado, para no chocar con el EXCLUDE
      // al reasignar sobre la misma franja/pista.
      for (const p of [...partidosOrigen, ...partidosDestino]) {
        await asignarPista(p.id, { pista: null, horaInicio: null, horaFin: null });
      }
      for (const p of partidosOrigen) {
        if (franjaDestino.pistasDisponibles.includes(p.pista)) {
          await asignarPista(p.id, { pista: p.pista, horaInicio: destinoId, horaFin: franjaDestino.horaFin, jornadaCuadrante: jornadaSel.jornada, reubicar: true });
        } else {
          sinSitio += 1;
        }
      }
      for (const p of partidosDestino) {
        if (franjaOrigen.pistasDisponibles.includes(p.pista)) {
          await asignarPista(p.id, { pista: p.pista, horaInicio: origenId, horaFin: franjaOrigen.horaFin, jornadaCuadrante: jornadaSel.jornada, reubicar: true });
        } else {
          sinSitio += 1;
        }
      }
      if (sinSitio > 0) {
        setAviso(`${sinSitio} partido(s) no tenían pista disponible en la franja destino y han vuelto a "sin colocar".`);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setProcesando(false);
      cargarPistas();
    }
  };

  const handleSacarAplazado = async (partidoId) => {
    if (!editable) return;
    setAviso(null);
    setProcesando(true);
    try {
      await asignarPista(partidoId, { pista: null, horaInicio: null, horaFin: null });
      setAviso('Partido sacado del cuadrante.');
    } catch (e) {
      setError(e.message);
    } finally {
      setProcesando(false);
      cargarPistas();
    }
  };

  const handleProponer = async () => {
    if (!editable || !datosPistas) return;
    setAviso(null);
    setProcesando(true);
    try {
      let modalidadAnterior = MODALIDADES[0];
      const indiceActual = jornadasReales.findIndex((j) => j.jornada === jornadaSel.jornada);
      const anterior = indiceActual > 0 ? jornadasReales[indiceActual - 1] : null;
      if (anterior) {
        try {
          const datosAnterior = await getPistasJornada(temporada, anterior.jornada);
          const colocadosAnterior = datosAnterior.partidos.filter((p) => p.hora_inicio);
          if (colocadosAnterior.length) {
            const primero = colocadosAnterior.reduce((a, b) => (a.hora_inicio <= b.hora_inicio ? a : b));
            modalidadAnterior = primero.modalidad;
          }
        } catch {
          // Sin jornada anterior legible: se queda con la primera modalidad de la lista.
        }
      }

      const resultado = proponerReparto(partidos, franjas, modalidadAnterior);
      const nuevos = resultado.partidos.filter((p) => {
        const original = partidos.find((o) => o.id === p.id);
        return original && !original.franjaId && p.franjaId;
      });

      for (const p of nuevos) {
        const franja = franjas.find((f) => f.id === p.franjaId);
        await asignarPista(p.id, { pista: p.pista, horaInicio: p.franjaId, horaFin: franja.horaFin });
      }

      setAviso(
        resultado.sinSitio.length
          ? `Propuesta aplicada empezando por ${resultado.modalidadElegida}. ${resultado.sinSitio.length} partido(s) no tenían hueco libre sin conflicto y siguen sin colocar.`
          : `Propuesta aplicada empezando por ${resultado.modalidadElegida}. Todos los partidos pendientes se han colocado.`,
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setProcesando(false);
      cargarPistas();
    }
  };

  const handlePublicar = async () => {
    setProcesando(true);
    try {
      await publicarJornada(temporada, jornadaSel.jornada);
      setAviso('Reparto publicado: ya es visible en el lado público.');
    } catch (e) {
      setError(e.message);
    } finally {
      setProcesando(false);
      cargarPistas();
    }
  };

  const handleResetear = async () => {
    setProcesando(true);
    try {
      await resetearJornada(temporada, jornadaSel.jornada);
      setAviso('Cuadrante vaciado y despublicado.');
    } catch (e) {
      setError(e.message);
    } finally {
      setProcesando(false);
      cargarPistas();
    }
  };

  const handleGuardarDisponibilidad = async (tramos) => {
    setGuardandoDisponibilidad(true);
    try {
      await reemplazarDisponibilidad(temporada, jornadaSel.jornada, tramos);
      setAviso('Disponibilidad de pistas guardada.');
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardandoDisponibilidad(false);
      cargarPistas();
    }
  };

  if (errorCalendario) return <main><p className="error">{errorCalendario}</p></main>;
  if (!temporada || cargandoCalendario) return <main><p>Cargando…</p></main>;

  return (
    <main className="page-wrapper">
      <div className="page-content competicion-pistas">
        <div className="page-header">
          <h2>Pistas de la jornada</h2>
          {datosPistas && (
            <span className={`cp-badge ${datosPistas.publicado ? 'cp-badge-publicado' : 'cp-badge-borrador'}`}>
              {datosPistas.publicado ? 'Publicado' : 'Borrador (solo interno)'}
            </span>
          )}
        </div>

        <label className="cp-jornada-selector">
          Jornada
          <select
            value={jornadaSel ? jornadaSel.jornada : ''}
            onChange={(e) => setJornadaSel({ jornada: Number(e.target.value) })}
          >
            {jornadasReales.map((j) => (
              <option key={j.jornada} value={j.jornada}>
                Jornada {j.jornada}
              </option>
            ))}
          </select>
        </label>
        {jornadaSel && (
          <div className="cp-jornada-selector cp-datos-jornada">
            <label>
              Fecha
              <input
                type="date"
                value={datosJornada.fecha}
                disabled={!puedeEditarDatos}
                onChange={(e) => setDatosJornada({ ...datosJornada, fecha: e.target.value })}
              />
            </label>
            <label>
              Sede
              <input
                type="text"
                maxLength={120}
                value={datosJornada.sede}
                disabled={!puedeEditarDatos}
                onChange={(e) => setDatosJornada({ ...datosJornada, sede: e.target.value })}
              />
            </label>
            {puedeEditarDatos && (
              <button type="button" onClick={handleGuardarDatos} disabled={guardandoDatos}>
                Guardar
              </button>
            )}
          </div>
        )}
        {!jornadasReales.length && <p>No hay calendario generado todavía para esta temporada.</p>}

        {error && <p className="cp-aviso cp-aviso-error">{error}</p>}
        {aviso && <p className="cp-aviso">{aviso}</p>}
        {hayConflictos && (
          <p className="cp-aviso cp-aviso-conflicto">
            ⚠ Hay jugadores convocados en dos pistas a la vez — celdas en rojo en el cuadrante.
          </p>
        )}

        {jornadaSel && <AplazadosJustificados partidos={aplazadosJustificados(calendario, jornadaSel.jornada)} />}

        {/* Solo pantalla de carga en la primera carga de la jornada: los
            refrescos tras arrastrar/proponer/publicar/resetear no deben
            desmontar todo el cuadrante, para que solo se note el cambio en
            la tabla y no un "parpadeo" de página completa. */}
        {cargandoPistas && !datosPistas && <p>Cargando cuadrante…</p>}

        {datosPistas && (
          <>
            {editable && (
              <DisponibilidadPanel
                key={jornadaSel.jornada}
                disponibilidad={datosPistas.disponibilidad}
                onGuardar={handleGuardarDisponibilidad}
                guardando={guardandoDisponibilidad}
              />
            )}

            {editable && (
              <div className="cp-toolbar">
                <button type="button" className="btn btn-primary" onClick={handleProponer} disabled={procesando}>Proponer</button>
                <button type="button" className="btn btn-success" onClick={handlePublicar} disabled={procesando || datosPistas.publicado}>Publicar</button>
                <button type="button" className="btn btn-danger" onClick={handleResetear} disabled={procesando}>Resetear</button>
              </div>
            )}

            {!franjas.length && <p className="cp-sin-publicar">No hay ninguna pista reservada todavía para esta jornada.</p>}

            {!!franjas.length && (
              <div className="tabla-wrapper">
                <table className="cuadrante-pistas">
                  <thead>
                    <tr>
                      <th scope="col">Hora</th>
                      {PISTAS.map((pista) => <th key={pista} scope="col">Pista {pista}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {franjas.map((franja) => (
                      <FilaFranja
                        key={franja.id}
                        franja={franja}
                        pistas={PISTAS}
                        partidosPorCelda={partidosPorCelda}
                        conflictosPorCelda={conflictosPorCelda}
                        editable={editable}
                        onDropPartido={handleDropPartido}
                        onMoverFranja={handleMoverFranja}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pestanas.length > 0 && <div className="cp-tabs" role="tablist" aria-label="Partidos de la jornada">
              {pestanas.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  id={`cp-tab-${t.id}`}
                  aria-selected={pestanaActiva === t.id}
                  aria-controls={`cp-panel-${t.id}`}
                  className={`cp-tab${pestanaActiva === t.id ? ' cp-tab-activa' : ''}`}
                  onClick={() => setPestana(t.id)}
                >
                  {t.titulo}
                </button>
              ))}
            </div>}

            {/* Los paneles se ocultan en vez de desmontarse: AdelantarPartidos
                conserva así su jornada/modalidad elegidas al cambiar de pestaña. */}
            {editable && (
              <div role="tabpanel" id="cp-panel-pendientes" aria-labelledby="cp-tab-pendientes" hidden={pestanaActiva !== 'pendientes'}>
                <div className="cp-pool">
                  <div className="cp-pool-lista">
                    {pendientes.map((p) => (
                      <PartidoCard key={p.id} partido={p} editable conflicto={null} />
                    ))}
                    {!pendientes.length && <p className="cp-pool-vacio">No quedan partidos sin colocar.</p>}
                  </div>
                </div>
              </div>
            )}

            {editable && hayFuturas && (
              <div role="tabpanel" id="cp-panel-adelantar" aria-labelledby="cp-tab-adelantar" hidden={pestanaActiva !== 'adelantar'}>
                <AdelantarPartidos
                  temporada={temporada}
                  jornadasFuturas={jornadasFuturas}
                  rosterPorParticipante={rosterPorParticipante}
                  fechaCuadrante={datosJornada.fecha}
                  recarga={datosPistas}
                />
              </div>
            )}

            {aplazadosPropios.length > 0 && (
              <div role="tabpanel" id="cp-panel-aplazados" aria-labelledby="cp-tab-aplazados" hidden={pestanaActiva !== 'aplazados'}>
                <AplazadosDeJornada
                  propios={aplazadosPropios}
                  colocados={aplazadosColocados}
                  onSacar={editable ? handleSacarAplazado : null}
                  procesando={procesando}
                />
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default CompeticionPistas;
